'use client'

import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { ModernHeader } from './ModernHeader'
import { ModernHero } from './ModernHero'
import { ModernProducts } from './ModernProducts'
import { ModernStory } from './ModernStory'
import { ModernCertificates } from './ModernCertificates'
import { ModernTestimonials } from './ModernTestimonials'
import { ModernNewsletter } from './ModernNewsletter'
import { ModernFooter } from './ModernFooter'
import { ModernCart } from './ModernCart'
import type { CartItem, Product } from './types'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuth } from '@/lib/auth/AuthContext'
import { checkoutOrder } from '@/lib/shop/api'
import { toast } from 'sonner'
import { useDefaultCurrency } from '@/lib/useDefaultCurrency'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import type { ApiTestimonial } from '@/lib/shop/api'

const fetcher = (url: string) => fetch(url, { cache: 'no-store' }).then((r) => r.json())

export function DelicesHome() {
  const router = useRouter()
  const params = useSearchParams()
  const { user } = useAuth()
  const { currency: defaultCurrency } = useDefaultCurrency()
  const { currencyCode } = useLanguage()
  const [cartItems, setCartItems] = useState<CartItem[]>([])
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutFields, setCheckoutFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    shippingAddress: '',
    billingAddress: '',
  })
  const [choiceResetToken, setChoiceResetToken] = useState(0)
  const [cartHydrated, setCartHydrated] = useState(false)
  const { data, error } = useSWR<{ testimonials: ApiTestimonial[] }>('/api/shop/testimonials', fetcher)

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0)
  const testimonials = data?.testimonials?.length ? data.testimonials : []
  const hasTestimonials = !error && testimonials.length > 0

  useEffect(() => {
    if (typeof window === 'undefined') return
    const raw = localStorage.getItem('syris_cart')
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as CartItem[]
        setCartItems(parsed)
      } catch {}
    }
    setCartHydrated(true)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!cartHydrated) return
    localStorage.setItem('syris_cart', JSON.stringify(cartItems))
  }, [cartItems, cartHydrated])

  useEffect(() => {
    if (params?.get('checkout') === '1' && cartItems.length > 0) {
      setIsCartOpen(true)
    }
  }, [params, cartItems.length])

  const addToCart = (product: Product) => {
    setCartItems((items) => {
      const existing = items.find((item) => item.id === product.id)
      if (existing) {
        return items.map((item) => (item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item))
      }
      return [...items, { ...product, quantity: 1 }]
    })
  }

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      setCartItems((items) => items.filter((item) => item.id !== id))
      return
    }
    setCartItems((items) => items.map((item) => (item.id === id ? { ...item, quantity } : item)))
  }

  const removeFromCart = (id: string) => {
    setCartItems((items) => items.filter((item) => item.id !== id))
  }

  const handleCheckoutAuthenticated = async () => {
    if (!cartItems.length) {
      toast.error('Votre panier est vide')
      return
    }
    if (!user) {
      router.push('/login?next=/%3Fcheckout%3D1')
      setIsCartOpen(false)
      return
    }
    if (!user.emailVerifiedAt) {
      toast.error('Veuillez vérifier votre email avant de passer commande')
      return
    }
    const missing: string[] = []
    if (!user.phone) missing.push('téléphone')
    if (!user.address) missing.push('adresse')
    if (missing.length) {
      toast.error(`Complétez votre ${missing.join(' et ')} dans votre profil pour passer commande.`)
      router.push('/account/profile')
      return
    }
    setCheckoutLoading(true)
    try {
      const res = await checkoutOrder({
        items: cartItems.map((item) => ({ productId: item.id, quantity: item.quantity })),
        buyerEmail: user.email,
        buyerName: user.name || undefined,
        buyerPhone: user.phone || undefined,
        shippingAddress: user.address || undefined,
        billingAddress: user.address || undefined,
        currency: currencyCode,
      })
      setCheckoutLoading(false)
      if (res.ok) {
        toast.success('Commande passée avec succès')
        setCartItems([])
        if (typeof window !== 'undefined') localStorage.removeItem('syris_cart')
        setIsCartOpen(false)
        router.push('/account/orders')
      } else {
        toast.error((res.body as any)?.error || 'Impossible de passer la commande')
      }
    } catch (e) {
      setCheckoutLoading(false)
      toast.error('Impossible de passer la commande')
    }
  }

  const handleGuestCheckout = async () => {
    if (!cartItems.length) {
      toast.error('Votre panier est vide')
      return
    }
    if (!checkoutFields.firstName || !checkoutFields.lastName || !checkoutFields.phone || !checkoutFields.shippingAddress) {
      toast.error('Merci de renseigner prénom, nom, téléphone et adresse de livraison')
      return
    }
    setCheckoutLoading(true)
    try {
      const res = await checkoutOrder({
        items: cartItems.map((item) => ({ productId: item.id, quantity: item.quantity })),
        buyerEmail: checkoutFields.email || undefined,
        buyerName: `${checkoutFields.firstName} ${checkoutFields.lastName}`.trim(),
        buyerFirstName: checkoutFields.firstName,
        buyerLastName: checkoutFields.lastName,
        buyerPhone: checkoutFields.phone,
        shippingAddress: checkoutFields.shippingAddress,
        billingAddress: checkoutFields.billingAddress || checkoutFields.shippingAddress,
        currency: currencyCode,
      })
      setCheckoutLoading(false)
      if (res.ok) {
        toast.success('Commande passée en invité')
        setCartItems([])
        if (typeof window !== 'undefined') localStorage.removeItem('syris_cart')
        setIsCartOpen(false)
        setChoiceResetToken((n) => n + 1)
        router.push('/')
      } else {
        toast.error((res.body as any)?.error || 'Impossible de passer la commande')
      }
    } catch (e) {
      setCheckoutLoading(false)
      toast.error('Impossible de passer la commande')
    }
  }

  useEffect(() => {
    const scrollToHash = () => {
      if (typeof window === 'undefined') return
      const hash = window.location.hash.replace('#', '')
      if (!hash) return
      const el = document.getElementById(hash)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    // initial on mount
    scrollToHash()
    // listen to hash changes when navigating from other pages
    window.addEventListener('hashchange', scrollToHash)
    return () => window.removeEventListener('hashchange', scrollToHash)
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModernHeader cartCount={cartCount} onCartClick={() => setIsCartOpen(true)} hasTestimonials={hasTestimonials} />
      <main>
        <ModernHero />
        <ModernProducts onAddToCart={addToCart} />
        <ModernStory />
        <ModernCertificates />
        {hasTestimonials ? <ModernTestimonials testimonials={testimonials} /> : null}
        <ModernNewsletter />
      </main>
      <ModernFooter hasTestimonials={hasTestimonials} />
      <ModernCart
        items={cartItems}
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
        onCheckout={handleCheckoutAuthenticated}
        shouldShowChoice={!user}
        resetChoiceToken={choiceResetToken}
        onGuestCheckout={handleGuestCheckout}
        checkoutLoading={checkoutLoading}
        showCheckoutForm={false}
        checkoutFields={checkoutFields}
        onCheckoutFieldChange={(field, value) => setCheckoutFields((f) => ({ ...f, [field]: value }))}
        currencyCode={defaultCurrency?.code}
        currencySymbol={defaultCurrency?.symbol}
      />
    </div>
  )
}
